'use-strict';

import axios from 'axios';
import MicroModal from 'micromodal';
import Pristine from 'pristinejs';
import { debounce } from 'lodash';
import {
  serialize,
  openModalWhenReady,
  isDealerPage,
  logErrorToSentry,
  disableScroll,
} from '../helpers/helpers';
import { pushDataLayerEvent } from '../helpers/data-layer';
import { fireAdobeAnalyticsEvent } from '../helpers/adobe-analytics';
import { createFormAnalytics, watchModalOpen } from '../helpers/form-analytics';
import {
  CONTACT_REP_HASH,
  CONTACT_REP_OPEN_EVENT,
} from '../helpers/contact-rep-modal';
import { repPhotoMarkup, swapFailedRepPhotos } from '../helpers/rep-photo';
import formFindRep from './form-find-rep';
import formLabelAnimation from './form-label-animation';

const formContactRep = (function() {
  const modalName = 'modal-contact-rep',
    modalContainer = document.querySelector(`#${modalName} .modal__container`),
    contactForm = document.getElementById('contact-rep'),
    whosMyRepButton = document.querySelector('.contact-form .js-find-rep'),
    subscribeCheckbox = document.querySelector('.js-form-checkbox'),
    inputHelper = document.querySelector('.input-helper'),
    inputHelperContent = document.querySelector('.input-helper__content'),
    repCardContainer = document.querySelector('.js-rep-card'),
    dealerCardContainer = document.querySelector('.js-dealer-card'),
    panelForm = document.querySelector(`#${modalName} .panel__group--form`),
    panelConfirmation = document.querySelector('.panel__group--confirmation'),
    formMessage = document.getElementById('contact-message'),
    formMessageCopy = document.querySelector('.js-form-message'),
    inputSelectorClass = '.custom-input',
    untouchedClass = 'is-untouched',
    visibleClass = 'is-visible',
    disabledClass = 'is-disabled',
    hiddenClass = 'is-hidden',
    errorColor = 'text-brand',
    successColor = 'text-success',
    apiHost = '//api.3-form.com',
    repApi = `${apiHost}/rep_search/?`,
    contactApi = `${apiHost}/contact_rep_emails`,
    subscribeApi = `${apiHost}/marketing_subscribers`,
    mqMd = window.matchMedia('(min-width: 768px)'),
    formAnalytics = createFormAnalytics(
      'Contact|Contact a Consultant',
      () => contactForm
    ),
    newsletterAnalytics = createFormAnalytics(
      'Contact|Newsletter Signup',
      () => contactForm
    );

  let formInputs,
    countryDropdown,
    countryDropdownContainer,
    zipcodeField,
    zipcodeContainer,
    dealerBranches = [],
    defaultConfig = {
      classTo: 'custom-input',
      errorClass: 'has-danger',
      successClass: 'has-success',
      // class of the parent element where error text element is appended
      errorTextParent: 'custom-input',
      // type of element to create for the error text
      errorTextTag: 'div',
      // class of the error text element
      errorTextClass: 'text-help',
    };
  /**
   *
   * @param repData - rep data
   */
  function repCardTemplate(repData) {
    const repInfo = repData.rep;
    return `
      <div class="content-card__content">
        <div class="flex mt-6">
          <div class="w-1/5 mr-6">
            <div class="rep-form__image--outer">
              ${repPhotoMarkup(repInfo.image)}
            </div>
          </div>
          <div class="w-4/5">
            <ul class="list-reset">
              <li class="mb-4">
                <p class="p font-medium">
                  ${repInfo.fname} ${repInfo.lname}
                </p>
                <p class="p">Materials Consultant${
                  repInfo.territory ? `, ${repInfo.territory}` : ''
                }</p>
              </li>
              <li class="mb-2">
                <svg class="icon icon-telephone mr-2">
                  <use xlink:href="#icon-telephone"></use>
                </svg>
                <a href="tel:${repInfo.phone}" class="a" tabindex="-1">
                  ${repInfo.phone}
                </a>
              </li>
              <li class="mb-2">
                <svg class="icon icon-email mr-2">
                  <use xlink:href="#icon-email"></use>
                </svg>
                <a href="mailto:${repInfo.email}" class="a" tabindex="-1">
                  ${repInfo.email}
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>
    `;
  }
  function dealerCardTemplate(dealer) {
    return `
      <div class="content-card__content">
        <div class="flex mt-6">
          <div class="w-1/5 mx-6">
            <div class="rep-form__image--outer">
              <div class="rep-form__image">
                <svg class="icon icon-log-in">
                  <use xlink:href="#icon-log-in"></use>
                </svg>
              </div>
            </div>
          </div>
          <div class="w-4/5">
            <ul class="list-reset">
              <li class="mb-4">
                <p class="p font-medium js-dealer-card-name">${dealer.name}</p>
                <p class="p">Dealer Partner Branch</p>
              </li>
            </ul>
          </div>
        </div>
      </div>
    `;
  }
  /**
   *
   * @param repData - rep data
   */
  function renderRepCard(repData) {
    // update rep card
    repCardContainer.innerHTML = '';
    dealerCardContainer.innerHTML = '';

    if (
      typeof repData !== 'object' ||
      repData.length < 1 ||
      typeof zipcodeField !== 'object' ||
      zipcodeField.value === ''
    )
      return;

    repCardContainer.innerHTML += repCardTemplate(repData[0]);

    if (!isDealerPage() || typeof repData[0].dealer_partner_branch !== 'object')
      return;

    dealerCardContainer.innerHTML += dealerCardTemplate(
      repData[0].dealer_partner_branch
    );
  }
  /**
   *
   * @param parent - input parent
   * @param className - input state class
   */
  function helperTextHandler(zipcodeContainer = null, repData = null) {
    // if handling zip code field
    if (zipcodeContainer) {
      zipcodeField = zipcodeContainer.querySelector('.js-form-zip');
      if (zipcodeField && zipcodeContainer.classList.contains(untouchedClass)) {
        if (inputHelper.classList.contains(successColor)) {
          inputHelper.classList.remove(successColor);
        }
        inputHelper.classList.add(visibleClass, errorColor);
        inputHelperContent.innerHTML = `
            This field is Required
        `;
      } else if (zipcodeField && inputHelper.classList.contains(errorColor)) {
        inputHelper.classList.remove(visibleClass, errorColor);
        inputHelperContent.innerHTML = '';
      }
    }
    // if handling rep data
  }
  /**
   *
   * @param e - event
   */
  function resetZipCode(e) {
    countryDropdown = e.currentTarget;
    zipcodeField =
      countryDropdown.parentNode.parentNode.children[1].children[1];
    if (zipcodeField.value !== '') {
      zipcodeField.value = '';
      formLabelAnimation.toggleValidClass(zipcodeField);
    }
  }
  /**
   *
   * @param e - event
   */
  function zipcodeAndHelperHandler(e) {
    const validClass = 'is-valid';
    countryDropdown = e.currentTarget;
    countryDropdownContainer = countryDropdown.parentNode;
    zipcodeField =
      countryDropdown.parentNode.parentNode.children[1].children[1];
    zipcodeContainer = zipcodeField.parentNode;
    // is zip code field untouched?
    if (
      zipcodeField.value === '' &&
      !zipcodeContainer.classList.contains(untouchedClass)
    ) {
      zipcodeContainer.classList.add(untouchedClass);
      helperTextHandler(zipcodeContainer, null);
    }
    // is zip code field valid?
    if (
      zipcodeField.value !== '' &&
      zipcodeContainer.classList.contains(untouchedClass)
    ) {
      zipcodeContainer.classList.remove(untouchedClass);
      helperTextHandler(zipcodeContainer, null);
    }
    // if dropdown is reset, reset zip code field
    if (countryDropdown.selectedIndex === 0) {
      if (
        zipcodeContainer.classList.contains(untouchedClass) ||
        (!countryDropdownContainer.classList.contains(validClass) &&
          zipcodeContainer.classList.contains(untouchedClass))
      ) {
        zipcodeContainer.classList.remove(untouchedClass);
        helperTextHandler(zipcodeContainer, null);
      }
    }
  }
  /**
   *
   * @param zipcodeField - zip code field
   */
  function toggleValidClass(zipcodeField) {
    zipcodeContainer = zipcodeField.parentNode;
    countryDropdown =
      zipcodeField.parentNode.previousElementSibling.children[0].children[1];
    // update zip code field state if dropdown or zip code inputs change
    if (
      zipcodeField.value !== '' ||
      (zipcodeField.value !== '' &&
        countryDropdown.selectedIndex >= 1 &&
        countryDropdown.selectedIndex !== null)
    ) {
      zipcodeContainer.classList.remove(untouchedClass);
      helperTextHandler(zipcodeContainer, null);
    } else {
      if (whosMyRepButton.classList.contains(disabledClass)) {
        whosMyRepButton.classList.remove(disabledClass);
        whosMyRepButton.setAttribute('tabindex', 0);
      }
      if (
        inputHelper.classList.contains(visibleClass) &&
        inputHelper.classList.contains(successColor)
      ) {
        inputHelper.classList.remove(visibleClass, successColor);
        inputHelperContent.innerHTML = '';
      }
      if (
        countryDropdown.selectedIndex >= 1 &&
        countryDropdown.selectedIndex !== null
      ) {
        zipcodeContainer.classList.add(untouchedClass);
        helperTextHandler(zipcodeContainer, null);
      }
    }
  }
  /**
   *
   * @param e - event
   */
  function classToggleHandler(e) {
    zipcodeField = e.currentTarget;
    zipcodeContainer = zipcodeField.parentNode;
    if (e.type === 'focus') {
      zipcodeContainer.classList.remove(untouchedClass);
      helperTextHandler(zipcodeContainer, null);
    } else if (e.type === 'blur') {
      toggleValidClass(zipcodeField);
    }
  }
  /**
   *
   * @param url - rep info API
   * @param formData - form data
   */
  const getApiData = (url, formData) => axios.get(url + formData);

  /**
   *
   * @param e - event
   */
  function pullRepData(e) {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    let formData = serialize(contactForm);
    formData = formData
      .substring(formData.indexOf('country'))
      .split('&message')[0];

    getApiData(repApi, formData)
      .then(response => {
        const repData = response.data;

        if (repData) {
          repData.forEach(rep => {
            if (
              isDealerPage() &&
              typeof rep.dealer_partner_branch === 'object' &&
              rep.dealer_partner_branch.name
            ) {
              dealerBranches.push(rep.dealer_partner_branch.name);
            }
          });
        }

        helperTextHandler(null, repData);
        renderRepCard(repData);
      })
      .catch(error => logErrorToSentry(error));
  }
  function postFormDataToApi() {
    // Set up form message to show dealer branch information if it's a dealer
    // page.
    if (isDealerPage() && dealerBranches.length > 0) {
      formMessage.value +=
        '<br><br>EVERYTHING AFTER HERE WAS ADDED AFTER ' +
        'THE CUSTOMER CLICKED SUBMIT<br><br>Dealer Partner Branches in the ' +
        `provided zip code:<br>&nbsp;${dealerBranches.join('<br>&nbsp;')}`;
    }

    // post contact form data
    const contactFormData = new FormData(contactForm);
    axios
      .post(contactApi, contactFormData)
      .then(response => {
        console.log(response);
        sendAnalyticsEvent('submit', 'Contact a Consultant');
        formAnalytics.submit();
      })
      .catch(error => {
        formAnalytics.error();
        logErrorToSentry(error);
      });
    // post subscription form data
    if (subscribeCheckbox.checked === true) {
      const formFilters = ['customer_email', 'zip', 'country'];
      const subscribeFormData = new FormData();
      // copy only relevant data from the contact form
      // for subscription form data
      contactFormData.forEach((value, key) => {
        for (let item of formFilters) {
          if (key === item) {
            if (key === 'customer_email') {
              subscribeFormData.append('email', value);
            } else {
              subscribeFormData.append(key, value);
            }
          }
        }
      });
      axios
        .post(subscribeApi, subscribeFormData)
        .then(response => {
          console.log(response);
          newsletterAnalytics.submit();
        })
        .catch(error => logErrorToSentry(error));
      sendAnalyticsEvent('subscribe', 'Newsletter');
    }
  }
  /**
   *
   * @param e - event
   */
  function formSubmitHandler() {
    const repLinks = repCardContainer.querySelectorAll('a');

    // postFormDataToApi changes formMessage.value because we want to send the
    // dealer branch information to the rep without displaying it to the user.
    // That information will be redundant with what's in the dealer card (and
    // also it's not something they entered so it may just be confusing or
    // otherwise weird to see).
    const originalFormMessage = formMessage.value;
    postFormDataToApi();

    if (document.activeElement) {
      document.activeElement.blur();
    }

    // update tabindex for rep card links,
    // which are disabled during form-editing view
    repLinks.forEach(link => link.setAttribute('tabindex', 0));
    // update form message
    formMessageCopy.innerHTML = originalFormMessage;
    // show/hide form + results blocks
    if (
      !panelForm.classList.contains(hiddenClass) &&
      panelConfirmation.classList.contains(hiddenClass)
    ) {
      panelConfirmation.classList.remove(hiddenClass);
      panelForm.classList.add(hiddenClass);
      modalContainer.style.maxHeight = '75vh';
      if (
        panelConfirmation.querySelectorAll('.js-rep-card').length === 1 &&
        mqMd.matches
      ) {
        modalContainer.style.overflowY = 'hidden';
      }
      modalContainer.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
    }
  }
  /**
   *
   * @param modal - modal
   */
  function resetForm(modal) {
    countryDropdown = modal.querySelector('.js-form-country');
    // show/hide form + results blocks
    if (
      panelForm.classList.contains(hiddenClass) &&
      !panelConfirmation.classList.contains(hiddenClass)
    ) {
      panelConfirmation.classList.add(hiddenClass);
      panelForm.classList.remove(hiddenClass);
      modalContainer.style.maxHeight = '100%';
      modalContainer.style.overflowY = 'auto';
    }
    // reset inputs
    [...formInputs].forEach(input => {
      // don't reset pre-filled request input
      if (input.id === 'contact-message') {
        return;
      } else {
        input.value = '';
        formLabelAnimation.toggleValidClass(input);
      }
    });
    countryDropdown.selectedIndex = null;
    formLabelAnimation.toggleValidClass(countryDropdown);
    if (subscribeCheckbox) subscribeCheckbox.checked = false;
    if (whosMyRepButton) {
      whosMyRepButton.classList.remove('is-disabled');
      whosMyRepButton.setAttribute('tabindex', 0);
    }
    if (zipcodeContainer) {
      zipcodeContainer.classList.remove('is-untouched');
    }
    if (inputHelper) {
      inputHelper.classList.remove('is-visible', 'text-brand', 'text-success');
      inputHelperContent.innerHTML = '';
    }
    // restore form value
    formMessage.value = localStorage.getItem(formMessage.name);
    if (formMessageCopy) formMessageCopy.innerHTML = '';
    if (repCardContainer) repCardContainer.innerHTML = '';
    // reset the form + results
    formFindRep.submitButtonValidation(modal);
  }

  // Every path that opens the modal has to run this, or the inputs never get
  // their validation listeners and the form only validates on submit.
  function prepareForm(modal) {
    resetForm(modal);
    [...formInputs].forEach(input => {
      input.addEventListener(
        'keyup',
        debounce(function() {
          formFindRep.submitButtonValidation(modal);
        }, 300)
      );
      input.addEventListener(
        'change',
        debounce(function() {
          formFindRep.submitButtonValidation(modal);
        }, 300)
      );
    });
  }

  // Single way in, so the scroll lock and the form wiring cannot drift apart
  // between entry points. Returns false when the modal was already open, which
  // is how the callers know not to report a second open.
  function openContactRep(modal) {
    if (modal.classList.contains('is-open')) return false;

    prepareForm(modal);
    MicroModal.show(modalName, {
      // A configless show() bypasses the modal-panel callbacks, so the scroll
      // lock and the hash cleanup have to be repeated here. Leaving the hash
      // set would make the next link to it fire no hashchange.
      onShow: () => disableScroll(true),
      onClose: () => {
        disableScroll(false);
        if (window.location.hash !== CONTACT_REP_HASH) return;
        const { pathname, search } = window.location;
        history.replaceState(null, '', pathname + search);
      },
    });
    return true;
  }

  function sendAnalyticsEvent(action, category) {
    if (action === 'subscribe') {
      pushDataLayerEvent('newsletter_signup', { placement: category });
      return;
    }

    pushDataLayerEvent('generate_lead', { lead_type: 'contact_a_rep' });
  }

  function init() {
    const modal = document.getElementById(modalName),
      pristine = new Pristine(contactForm, defaultConfig);

    if (modal) {
      swapFailedRepPhotos();
      const submitButton = modal.querySelector('.js-form-submit');
      const closeButton = modal.querySelector('.js-close-panel');
      formInputs = modal.querySelectorAll(
        `${inputSelectorClass} input[type="text"],
        ${inputSelectorClass} input[type="email"],
        ${inputSelectorClass} select`
      );
      countryDropdown = modal.querySelector('.js-form-country');
      zipcodeField = modal.querySelector('.js-form-zip');
      // save form value
      localStorage.setItem(formMessage.name, formMessage.value);
      // when form is triggered
      document.addEventListener('click', function(e) {
        if (
          !e.target.classList.contains('js-contact-rep') ||
          (e.target.href && e.target.href != '#contact-rep')
        )
          return;

        // reset form & listen for completed form
        prepareForm(modal);
      });
      // The CTA components hand off here instead of showing the modal
      // themselves; they report their own click, so this path stays silent.
      document.addEventListener(CONTACT_REP_OPEN_EVENT, function() {
        openContactRep(modal);
      });
      // Nothing else reacts to the hash once the page has loaded, so a plain
      // "#contact-rep" link is inert without this. It is also the only entry
      // point with no CTA behind it to report the click, hence the events.
      window.addEventListener('hashchange', function() {
        if (window.location.hash !== CONTACT_REP_HASH) return;
        if (!openContactRep(modal)) return;

        fireAdobeAnalyticsEvent({
          type: 'click',
          detail: { eVar8: 'INP|Contact Rep|Modal Open' },
        });
        pushDataLayerEvent('contact_rep_modal_open', {
          placement: 'hash link',
        });
      });
      // country dropdown event listener
      countryDropdown.addEventListener('change', function(e) {
        resetZipCode(e);
        zipcodeAndHelperHandler(e);
      });
      // zip code field event listener
      zipcodeField.addEventListener('focus', function(e) {
        classToggleHandler(e);
      });
      zipcodeField.addEventListener('blur', function(e) {
        classToggleHandler(e);
      });
      zipcodeField.addEventListener(
        'keyup',
        debounce(function(e) {
          pullRepData(e);
        }, 300)
      );
      // On the form, not in prepareForm, which adds its listeners per open.
      contactForm.addEventListener('input', () => formAnalytics.start());
      watchModalOpen(modal, {
        onOpen: () => formAnalytics.view(),
        onClose: () => formAnalytics.end(),
      });
      submitButton.addEventListener('click', function(e) {
        const isValid = pristine.validate();
        e.preventDefault();
        if (isValid) formSubmitHandler(e);
        else formAnalytics.error();
      });
      // form resets
      closeButton.addEventListener('click', function() {
        resetForm(modal);
      });
      document.addEventListener('keydown', function(e) {
        if (e.keyCode === 27 && modal.classList.contains === 'is-open') {
          resetForm(modal);
        }
      });

      const openModal = window.location.hash === '#contact-rep';

      if (openModal) openModalWhenReady(modal);
    }
  }
  return {
    init,
  };
})();
export default formContactRep;
